/**
 * Resultado de la cuenta corriente (antigüedad de saldos) como panel legible.
 *
 * Las acciones de reporte —«Cuenta corriente», «Sólo lo vencido»— no cambian
 * ningún registro: su resultado ES el informe. Mostrarlo como un toast con el
 * número crudo ("total de 5705452.50 (0.00 vencido)") lo hacía ilegible y no se
 * sentía un ERP argentino. El servidor ya devuelve el saldo repartido por
 * antigüedad (`buckets` + `totals`), así que se dibuja esa tabla —una banda por
 * fila, importes en es-AR— en vez de una línea de texto.
 *
 * No hay lógica de negocio acá: los tramos, los importes y qué es "vencido" los
 * decidió el backend (`invoicing/services/aging.py`). Este componente solo los
 * formatea con el mismo helper de moneda que el resto de la app.
 */

import { useEffect, useRef } from 'react';

import { formatCurrency, formatDate } from '@/components/fields/format';
import { Button } from '@/components/shell/primitives';
import type { ActionResult } from '@/lib/types';

/** Un tramo tal como lo serializa el backend (`aging.Tramo`). */
interface TramoJson {
  label: string;
  from_days: number | null;
  to_days: number | null;
}

/** El informe de antigüedad, tal como llega en `results[0].data`. */
export interface AntiguedadJson {
  on_date?: string;
  kind?: string;
  buckets: TramoJson[];
  totals: string[];
  total: string;
  overdue: string;
  /** Nombre del contacto cuando el informe es de uno solo (la ficha de cliente). */
  contacto?: string;
}

const ETIQUETA_TIPO: Record<string, string> = {
  customer: 'como cliente',
  supplier: 'como proveedor',
  all: 'como cliente y proveedor',
};

/**
 * Extrae el informe de antigüedad de la respuesta de una acción, o `null` si la
 * acción no devolvió uno.
 *
 * Se detecta por la FORMA del dato (tramos + totales alineados y un total), no
 * por el nombre de la acción: así lo cubren tanto «Cuenta corriente» como «Sólo
 * lo vencido» sin acoplar el frontend a nombres técnicos del backend.
 */
export function leerAntiguedad(resultado: ActionResult | undefined): AntiguedadJson | null {
  if (resultado === undefined) return null;
  const resultados = (resultado as { results?: unknown }).results;
  if (!Array.isArray(resultados) || resultados.length === 0) return null;
  const datos = (resultados[0] as { data?: unknown }).data;
  if (datos === null || typeof datos !== 'object') return null;
  const d = datos as Record<string, unknown>;
  if (
    Array.isArray(d.buckets) &&
    Array.isArray(d.totals) &&
    d.buckets.length === d.totals.length &&
    d.buckets.length > 0 &&
    typeof d.total === 'string' &&
    d.buckets.every((b) => b !== null && typeof b === 'object' && typeof (b as TramoJson).label === 'string') &&
    d.totals.every((t) => typeof t === 'string')
  ) {
    // Un informe de un solo contacto (la acción de la ficha de cliente) trae su
    // nombre en `partners[0]`; sirve para el subtítulo del panel.
    const partners = d.partners;
    const contacto =
      Array.isArray(partners) && partners.length === 1 && partners[0] !== null && typeof partners[0] === 'object'
        ? (partners[0] as { partner_name?: unknown }).partner_name
        : undefined;
    return {
      buckets: d.buckets as TramoJson[],
      totals: d.totals as string[],
      total: d.total,
      overdue: typeof d.overdue === 'string' ? d.overdue : '0',
      ...(typeof d.on_date === 'string' ? { on_date: d.on_date } : {}),
      ...(typeof d.kind === 'string' ? { kind: d.kind } : {}),
      ...(typeof contacto === 'string' ? { contacto } : {}),
    };
  }
  return null;
}

/** `true` si el importe (string decimal) es distinto de cero. */
function tieneSaldo(importe: string): boolean {
  const numero = Number(importe);
  return Number.isFinite(numero) && numero !== 0;
}

export interface AgingResultDialogProps {
  /** Título de la acción que lo produjo, p. ej. «Cuenta corriente». */
  titulo: string;
  /** Sobre quién se corrió, para el subtítulo. */
  contactoNombre?: string;
  datos: AntiguedadJson | null;
  onClose: () => void;
}

export function AgingResultDialog({ titulo, contactoNombre, datos, onClose }: AgingResultDialogProps) {
  const panel = useRef<HTMLDivElement>(null);
  const focoPrevio = useRef<HTMLElement | null>(null);
  const abierto = datos !== null;

  useEffect(() => {
    if (!abierto) return;
    focoPrevio.current = document.activeElement as HTMLElement | null;
    const alTeclear = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') {
        evento.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', alTeclear, true);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const temporizador = window.setTimeout(() => {
      panel.current?.querySelector<HTMLElement>('#antiguedad-cerrar')?.focus();
    }, 0);
    return () => {
      document.removeEventListener('keydown', alTeclear, true);
      document.body.style.overflow = overflow;
      window.clearTimeout(temporizador);
      focoPrevio.current?.focus();
    };
  }, [abierto, onClose]);

  if (datos === null) return null;

  const sinSaldo = !tieneSaldo(datos.total);
  const tipo = datos.kind !== undefined ? ETIQUETA_TIPO[datos.kind] : undefined;
  const subtitulo = [
    contactoNombre,
    tipo,
    datos.on_date !== undefined && datos.on_date !== '' ? `al ${formatDate(datos.on_date)}` : null,
  ]
    .filter((parte) => parte != null && parte !== '')
    .join(' · ');

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center">
      <div className="absolute inset-0 bg-fg/30 animate-fade-in" onClick={onClose} aria-hidden="true" />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="antiguedad-titulo"
        className="relative w-full max-w-lg rounded-xl border border-line bg-surface p-5 shadow-lg animate-slide-up"
      >
        <h2 id="antiguedad-titulo" className="text-lg font-semibold">
          {titulo}
        </h2>
        {subtitulo !== '' && <p className="mt-1 text-sm text-fg-muted">{subtitulo}</p>}

        {sinSaldo ? (
          <p className="mt-4 rounded-lg border border-dashed border-line px-3 py-6 text-center text-sm text-fg-muted">
            No hay saldos pendientes.
          </p>
        ) : (
          <div className="mt-4 overflow-hidden rounded-lg border border-line">
            <table className="w-full border-collapse text-sm">
              <caption className="sr-only">Saldo por antigüedad</caption>
              <thead>
                <tr className="border-b border-line bg-app-subtle text-left">
                  <th scope="col" className="px-3 py-2 font-medium text-fg-muted">
                    Antigüedad
                  </th>
                  <th scope="col" className="px-3 py-2 text-right font-medium text-fg-muted">
                    Saldo
                  </th>
                </tr>
              </thead>
              <tbody>
                {datos.buckets.map((tramo, indice) => {
                  const importe = datos.totals[indice] ?? '0';
                  const apagado = !tieneSaldo(importe);
                  return (
                    <tr key={tramo.label} className="border-b border-line last:border-0">
                      <th
                        scope="row"
                        className={`px-3 py-2 text-left font-normal ${apagado ? 'text-fg-faint' : ''}`}
                      >
                        {tramo.label}
                      </th>
                      <td
                        className={`px-3 py-2 text-right tabular-nums ${apagado ? 'text-fg-faint' : ''}`}
                      >
                        {formatCurrency(importe)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-line bg-app-subtle font-semibold">
                  <th scope="row" className="px-3 py-2 text-left">
                    Total
                  </th>
                  <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(datos.total)}</td>
                </tr>
                {tieneSaldo(datos.overdue) && (
                  <tr className="border-t border-line">
                    <th scope="row" className="px-3 py-2 text-left font-normal text-danger">
                      De los cuales vencido
                    </th>
                    <td className="px-3 py-2 text-right font-medium tabular-nums text-danger">
                      {formatCurrency(datos.overdue)}
                    </td>
                  </tr>
                )}
              </tfoot>
            </table>
          </div>
        )}

        <div className="mt-5 flex justify-end">
          <Button variant="primary" onClick={onClose} id="antiguedad-cerrar">
            Cerrar
          </Button>
        </div>
      </div>
    </div>
  );
}
